import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { trackEvent } from '../utils/analytics';
import { ANALYTICS_EVENTS } from '../utils/analyticsEvents';
import { buildTitle, DEFAULT_OG_IMAGE, SITE_URL } from '../utils/seo';

const CHATYDESK_ORIGIN = 'https://chatydesk.com';
const CHATYDESK_SLUG = 'sistakyerewaa';

function formatPrice(amount, currency) {
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency,
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

export default function Book() {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState('');
  const [embedHeight, setEmbedHeight] = useState(600);
  const [offerings, setOfferings] = useState(null);
  const [selectedOffering, setSelectedOffering] = useState(null);
  const embedRef = useRef(null);
  const bookRef = useRef(null);

  // Live services, prices and durations from Chatydesk. If this fails the cards simply
  // don't render and the embed below still lists the offerings itself.
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${CHATYDESK_ORIGIN}/api/embed/${CHATYDESK_SLUG}`, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
      .then((data) => {
        if (Array.isArray(data.offerings)) {
          setOfferings({ currency: data.currency || 'EUR', items: data.offerings.filter((o) => o.id) });
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  function chooseOffering(id) {
    setSelectedOffering(id);
    trackEvent(ANALYTICS_EVENTS.BOOK_OFFERING_SELECT, { offering_id: id });
    bookRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Chatydesk embed posts { type: 'chatydesk:height', height } as its content changes.
  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== CHATYDESK_ORIGIN) return;
      if (e.source !== embedRef.current?.contentWindow) return;
      if (e.data?.type === 'chatydesk:height' && Number.isFinite(e.data.height)) {
        setEmbedHeight(Math.max(200, e.data.height));
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    const form = e.currentTarget;
    setSending(true);
    setFormError('');
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.fromEntries(new FormData(form))),
      });
      const result = await response.json();
      if (!response.ok || !result.ok) throw new Error(result.message);
      trackEvent(ANALYTICS_EVENTS.BOOK_FORM_SUBMIT);
      setSubmitted(true);
    } catch (err) {
      setFormError(err?.message || 'Could not send your message. Please try again.');
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <Helmet>
        <title>{buildTitle('Book Consultation')}</title>
        <meta
          name="description"
          content="Book a consultation with Sista Kyerewaa for integration guidance, practical next steps, and community-focused support in Germany."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/book`} />
        <meta property="og:title" content={buildTitle('Book Consultation')} />
        <meta property="og:description" content="Book a consultation with Sista Kyerewaa for integration guidance and practical support in Germany." />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={buildTitle('Book Consultation')} />
        <meta property="twitter:description" content="Book a consultation with Sista Kyerewaa for integration guidance and practical support in Germany." />
        <meta property="twitter:image" content={DEFAULT_OG_IMAGE} />
        <link rel="canonical" href={`${SITE_URL}/book`} />
      </Helmet>
      <main className="max-w-5xl mx-auto px-6 py-10 md:py-12">
        <div className="text-center mb-8">
          <p className="text-xs uppercase tracking-[0.35em] text-[#7b7671]">Book</p>
          <h1 className="font-display text-4xl md:text-5xl mt-4 text-[#1c1b1a]">Let&apos;s connect & book</h1>
          <p className="text-[#6a6661] mt-4">
            Share a question, collaboration request, or book a session directly below.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-[1.1fr_0.9fr] mb-6">
          <div id="contact-form">
            {submitted ? (
              <div className="border border-stone-200 bg-white/70 p-6 text-center">
                <h2 className="font-display text-2xl mb-2 text-[#2d2b29]">Thank you</h2>
                <p className="text-[#6a6661]">Your message has been received. We will get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="border border-stone-200 bg-white/70 p-6 flex flex-col gap-4">
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  required
                  className="px-4 py-3 border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#c9c5bf]"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  required
                  className="px-4 py-3 border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#c9c5bf]"
                />
                <input type="text" name="hp_check_x7" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
                <textarea
                  name="message"
                  placeholder="Your Message"
                  rows={5}
                  required
                  className="px-4 py-3 border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#c9c5bf]"
                />
                {formError && <p role="alert" className="text-sm text-red-700">{formError}</p>}
                <button
                  type="submit"
                  disabled={sending}
                  className="bg-[#2d2b29] text-[#f5f4f2] uppercase tracking-[0.25em] text-xs py-3 hover:bg-[#1f1d1b] transition-colors"
                >
                  {sending ? 'Sending…' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
          <div className="border border-stone-200 bg-white/70 p-6">
            <p className="text-xs uppercase tracking-[0.25em] text-[#7b7671]">Choose a service</p>
            <div className="grid gap-4 mt-4">
              {offerings?.items.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => chooseOffering(o.id)}
                  aria-pressed={selectedOffering === o.id}
                  className={`text-left border bg-white/80 p-4 hover:border-[#bdb7b0] transition-colors ${
                    selectedOffering === o.id ? 'border-[#2d2b29]' : 'border-stone-200'
                  }`}
                >
                  <p className="text-xs uppercase tracking-[0.2em] text-[#7b7671]">
                    {o.priceAmount > 0 ? formatPrice(o.priceAmount, offerings.currency) : 'Free'} · {o.durationMinutes} min
                  </p>
                  <h2 className="font-display text-xl text-[#2d2b29] mt-3">{o.title}</h2>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#7b7671] mt-4">Pick a time</p>
                </button>
              ))}
              {[
                {
                  title: 'Document Support',
                  copy: 'Help filling forms and organizing requirements. Educational support only.',
                  price: 'Starting at €3 per page',
                },
                {
                  title: 'Speaking & Workshops',
                  copy: 'Invite Sista Kyerewaa to speak at your event or community program.',
                  price: 'Request pricing',
                },
              ].map((service) => (
                <a
                  key={service.title}
                  href="#contact-form"
                  className="border border-stone-200 bg-white/80 p-4 hover:border-[#bdb7b0] transition-colors"
                >
                  <p className="text-xs uppercase tracking-[0.2em] text-[#7b7671]">{service.price}</p>
                  <h2 className="font-display text-xl text-[#2d2b29] mt-3">{service.title}</h2>
                  <p className="text-[#6a6661] mt-2">{service.copy}</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#7b7671] mt-4">Send a message</p>
                </a>
              ))}
            </div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#7b7671] mt-6">
              Educational support only. For legal or medical advice, consult a licensed professional.
            </p>
          </div>
        </div>

        <div id="book-session" ref={bookRef} className="border border-stone-200 bg-white/70 p-6">
          <p className="text-xs uppercase tracking-[0.25em] text-[#7b7671] mb-4">Book a session</p>
          <iframe
            ref={embedRef}
            src={`${CHATYDESK_ORIGIN}/embed/${CHATYDESK_SLUG}${selectedOffering ? `?offering=${selectedOffering}` : ''}`}
            title="Book a session with Sista Kyerewaa"
            style={{ display: 'block', margin: '0 auto', width: '100%', maxWidth: '640px', height: `${embedHeight}px`, border: 0, borderRadius: '12px' }}
            loading="lazy"
            allow="payment"
          />
        </div>
      </main>
    </>
  );
}
